import React, { useState, useEffect, useCallback } from 'react';
import RingtoneForm from './components/RingtoneForm';
import RingtoneCard from './components/RingtoneCard';
import RingtoneSongTable from './components/RingtoneSongTable';
import SharedHeader from '../../components/shared/SharedHeader';
import ProtectedPage from '../../components/shared/ProtectedPage';
import { usePermissions } from '../../utils/usePermissions';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import LoadingOverlay from '../../components/shared/LoadingOverlay';
import { apiClient } from '../../api/apiClient';
import { 
  GET_RINGTONES_URL, 
  CREATE_RINGTONE_URL, 
  UPDATE_RINGTONE_URL, 
  DELETE_RINGTONE_URL, 
  INACTIVATE_RINGTONE_URL,
  ACTIVATE_RINGTONE_URL,
  GET_RINGTONE_SONGS_URL 
} from '../../api/ringtone';
import { toast } from '../../components/shared/toast';
import en from '../../translations/en.json';
import EmptyState from '../../components/shared/EmptyState';
import Loader from '../../components/shared/Loader';
import './Ringtone.css';

function Ringtone({ onSelectSong }) {
  const { canCreate, canEdit, canDelete } = usePermissions();
  const canCreateRingtones = canCreate('ringtones');
  const canEditRingtones = canEdit('ringtones');
  const canDeleteRingtones = canDelete('ringtones');

  const [ringtones, setRingtones] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [selectedOperator, setSelectedOperator] = useState(null);
  
  // Detailed Songs state
  const [detailSongs, setDetailSongs] = useState([]);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalSongs, setDetailTotalSongs] = useState(0);
  const [isSongsLoading, setIsSongsLoading] = useState(false);

  const [isAddingRingtone, setIsAddingRingtone] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Modals state — Save duplicate confirmation
  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);
  const [pendingRingtoneData, setPendingRingtoneData] = useState(null);

  // Modals state — Delete
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [operatorToDelete, setOperatorToDelete] = useState(null);
  const [deleteDependencyWarningOpen, setDeleteDependencyWarningOpen] = useState(false);
  const [pendingDeleteSongs, setPendingDeleteSongs] = useState([]);

  // Modals state — Inactivate
  const [showConfirmInactivateModal, setShowConfirmInactivateModal] = useState(false);
  const [operatorToInactivate, setOperatorToInactivate] = useState(null);
  const [inactivateDependencyWarningOpen, setInactivateDependencyWarningOpen] = useState(false);
  const [pendingInactivateSongs, setPendingInactivateSongs] = useState([]);

  // Modals state — Activate
  const [showConfirmActivateModal, setShowConfirmActivateModal] = useState(false);
  const [operatorToActivate, setOperatorToActivate] = useState(null);

  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const fetchRingtones = useCallback(async () => {
    try {
      setIsLoading(true);
      const url = `${GET_RINGTONES_URL}?page=${currentPage}&limit=20&search=${searchQuery}`;
      const response = await apiClient(url);
      if (response && response.ringtones) {
        setRingtones(response.ringtones);
        setTotalCount(response.totalCount || 0);
      }
    } catch (err) {
      console.error('Failed to fetch ringtones:', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery]);

  const [hasMoreRingtoneSongs, setHasMoreRingtoneSongs] = useState(true);
  const [isSongsLoadingMore, setIsSongsLoadingMore] = useState(false);

  const fetchRingtoneSongs = useCallback(async (pageNum = 1, append = false) => {
    if (!selectedOperator) return;
    try {
      if (append) setIsSongsLoadingMore(true);
      else setIsSongsLoading(true);

      const url = `${GET_RINGTONE_SONGS_URL(selectedOperator.id)}?page=${pageNum}&limit=20`;
      const response = await apiClient(url);
      if (response && response.songs) {
        setDetailSongs(prev => append ? [...prev, ...response.songs] : (response.songs || []));
        setDetailTotalSongs(response.totalCount || 0);
        setHasMoreRingtoneSongs(response.songs.length === 20);
      }
    } catch (err) {
      console.error('Failed to fetch ringtone songs:', err);
      if (!append) {
        setDetailSongs([]);
        setDetailTotalSongs(0);
      }
    } finally {
      setIsSongsLoading(false);
      setIsSongsLoadingMore(false);
    }
  }, [selectedOperator]);

  useEffect(() => {
    if (!selectedOperator) {
      fetchRingtones();
    }
  }, [selectedOperator]);

  useEffect(() => {
    if (selectedOperator) {
      setDetailPage(1);
      fetchRingtoneSongs(1, false);
    }
  }, [selectedOperator]);

  const handleRingtoneSongsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasMoreRingtoneSongs && !isSongsLoadingMore && !isSongsLoading) {
      const nextPage = detailPage + 1;
      setDetailPage(nextPage);
      fetchRingtoneSongs(nextPage, true);
    }
  };

  // Reset pagination when search query changes
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setCurrentPage(1);
    setDetailPage(1);
  };

  const handleSaveRingtone = async (newData) => {
    const isDuplicate = ringtones.some(
      (r) => 
        r.name.toLowerCase() === newData.name.toLowerCase() && 
        (!editingOperator || editingOperator.name.toLowerCase() !== newData.name.toLowerCase())
    );
    
    if (isDuplicate) {
      setPendingRingtoneData(newData);
      setShowConfirmSaveModal(true);
    } else {
      await proceedSaveRingtone(newData);
    }
  };

  const handleConfirmSave = async () => {
    setShowConfirmSaveModal(false);
    if (pendingRingtoneData) {
      await proceedSaveRingtone(pendingRingtoneData);
      setPendingRingtoneData(null);
    }
  };

  const proceedSaveRingtone = async (payload) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', payload.name);
      if (payload.logo && payload.logo.file) {
        formData.append('logo', payload.logo.file);
      }
      
      if (editingOperator) {
        // Edit Mode
        await apiClient(UPDATE_RINGTONE_URL(editingOperator.id), {
          method: 'PUT',
          body: formData
        });
        toast.success('Ringtone operator updated successfully!');
      } else {
        // Create Mode
        await apiClient(CREATE_RINGTONE_URL, {
          method: 'POST',
          body: formData
        });
        toast.success('Ringtone operator created successfully!');
      }
      
      setIsAddingRingtone(false);
      setEditingOperator(null);
      fetchRingtones();
    } catch (err) {
      console.error('Failed to save ringtone operator:', err);
      toast.error('Failed to save operator: ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardEdit = (operator) => {
    setEditingOperator(operator);
  };

  // ────────────────────────────────────────────────
  //  INACTIVATE FLOW
  // ────────────────────────────────────────────────

  const handleInactivateOperator = (operator) => {
    setOperatorToInactivate(operator);
    setShowConfirmInactivateModal(true);
  };

  const handleConfirmInactivate = async () => {
    setShowConfirmInactivateModal(false);
    if (!operatorToInactivate) return;
    try {
      setIsLoading(true);
      // Call without force to check dependencies
      const response = await apiClient(INACTIVATE_RINGTONE_URL(operatorToInactivate.id), {
        method: 'PUT'
      });
      if (response && response.hasDependencies) {
        setPendingInactivateSongs(response.dependentSongs || []);
        setInactivateDependencyWarningOpen(true);
      } else {
        toast.success('Ringtone operator deactivated successfully!');
        fetchRingtones();
        setOperatorToInactivate(null);
      }
    } catch (err) {
      console.error('Inactivate check failed:', err);
      toast.error('Failed to inactivate operator: ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  const proceedForceInactivate = async () => {
    setInactivateDependencyWarningOpen(false);
    if (!operatorToInactivate) return;
    try {
      setIsLoading(true);
      await apiClient(`${INACTIVATE_RINGTONE_URL(operatorToInactivate.id)}?force=true`, {
        method: 'PUT'
      });
      toast.success('Ringtone operator and related mappings deactivated successfully!');
      fetchRingtones();
      setOperatorToInactivate(null);
      setPendingInactivateSongs([]);
    } catch (err) {
      console.error('Force inactivate failed:', err);
      toast.error('Failed to deactivate relationships.');
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //  ACTIVATE FLOW
  // ────────────────────────────────────────────────

  const handleActivateOperator = (operator) => {
    setOperatorToActivate(operator);
    setShowConfirmActivateModal(true);
  };

  const handleConfirmActivate = async () => {
    setShowConfirmActivateModal(false);
    if (!operatorToActivate) return;
    try {
      setIsLoading(true);
      await apiClient(ACTIVATE_RINGTONE_URL(operatorToActivate.id), {
        method: 'PUT'
      });
      toast.success('Ringtone operator activated successfully!');
      fetchRingtones();
      setOperatorToActivate(null);
    } catch (err) {
      console.error('Activate failed:', err);
      toast.error('Failed to activate operator: ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //  DELETE FLOW (soft delete — only for inactive)
  // ────────────────────────────────────────────────

  const handleDeleteOperator = (operator) => {
    setOperatorToDelete(operator);
    setShowConfirmDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowConfirmDeleteModal(false);
    if (!operatorToDelete) return;
    try {
      setIsLoading(true);
      // API call to delete (first without force to check dependencies)
      const response = await apiClient(DELETE_RINGTONE_URL(operatorToDelete.id), {
        method: 'DELETE'
      });
      if (response && response.hasDependencies) {
        setPendingDeleteSongs(response.dependentSongs || []);
        setDeleteDependencyWarningOpen(true);
      } else {
        toast.success('Ringtone operator deleted successfully!');
        fetchRingtones();
        setOperatorToDelete(null);
      }
    } catch (err) {
      console.error('Delete check failed:', err);
      toast.error('Failed to initiate delete: ' + (err.message || ''));
    } finally {
      setIsLoading(false);
    }
  };

  const proceedForceDelete = async () => {
    setDeleteDependencyWarningOpen(false);
    if (!operatorToDelete) return;
    try {
      setIsLoading(true);
      await apiClient(`${DELETE_RINGTONE_URL(operatorToDelete.id)}?force=true`, {
        method: 'DELETE'
      });
      toast.success('Ringtone operator and related mappings permanently deleted!');
      fetchRingtones();
      setOperatorToDelete(null);
      setPendingDeleteSongs([]);
    } catch (err) {
      console.error('Force delete failed:', err);
      toast.error('Failed to delete relationships.');
    } finally {
      setIsLoading(false);
    }
  };

  // ────────────────────────────────────────────────
  //  EXPORT
  // ────────────────────────────────────────────────

  const handleExportClick = async () => {
    try {
      setIsExporting(true);
      await new Promise(resolve => setTimeout(resolve, 800)); // export animation delay matching Artist tab

      if (selectedOperator) {
        // Export Related Songs
        const response = await apiClient(`${GET_RINGTONE_SONGS_URL(selectedOperator.id)}?export=true`);
        if (response && response.songs) {
          const headers = ['Song Name', 'Sinhala Name', 'Artist', 'Lyrics', 'Music', 'Album', 'Distributor', 'Release Date', 'ISRC Code', 'Version Type', 'Ownership', 'Conflicts'];
          const rows = response.songs.map(s => [
            s.name,
            s.nameSinhala || '',
            s.artist,
            s.lyrics,
            s.music,
            s.album,
            s.distributor,
            s.releaseDate,
            s.isrcCode,
            s.versionType,
            `${s.ownership}%`,
            s.conflicts || s.conflict || 'No'
          ]);

          const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
          ].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          const formattedName = selectedOperator.name.replace(/\s+/g, '_');
          link.setAttribute('download', `ringtone_songs_export_${formattedName}_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('Songs list exported successfully!');
        } else {
          toast.error('Failed to export songs.');
        }
      } else {
        // Export Ringtone Masters
        const response = await apiClient(`${GET_RINGTONES_URL}?export=true&search=${searchQuery}`);
        if (response && response.ringtones) {
          const headers = ['Operator Name', 'Status', 'Active Songs Count'];
          const rows = response.ringtones.map(r => [
            r.name,
            r.status,
            r.songCount
          ]);

          const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
          ].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', `ringtone_operators_export_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success('Ringtone operators exported successfully!');
        } else {
          toast.error('Failed to export operators.');
        }
      }
    } catch (err) {
      console.error('Export Error:', err);
      toast.error('Error exporting data.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleBackToGrid = () => {
    setSelectedOperator(null);
    setDetailPage(1);
    setDetailSongs([]);
  };

  if (isAddingRingtone || editingOperator) {
    return (
      <div className="ringtone-page-wrapper">
        <RingtoneForm 
          isModal={false}
          onCancel={() => {
            setIsAddingRingtone(false);
            setEditingOperator(null);
          }}
          onSave={handleSaveRingtone}
          existingNames={ringtones.map(r => r.name)}
          operator={editingOperator}
        />
      </div>
    );
  }

  // Build the breadcrumb title element for detail view
  const headerTitle = selectedOperator ? (
    <div className="ringtone-breadcrumb" aria-label="Breadcrumb">
      <button 
        type="button" 
        className="breadcrumb-btn home-btn" 
        onClick={handleBackToGrid} 
        aria-label="Home"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      </button>
      <span className="breadcrumb-separator">&gt;</span>
      <button 
        type="button" 
        className="breadcrumb-btn" 
        onClick={handleBackToGrid}
      >
        {t('ringtone.title')}
      </button>
      <span className="breadcrumb-separator">/</span>
      <span className="breadcrumb-active">{selectedOperator.shortName || selectedOperator.name}</span>
    </div>
  ) : (
    t('ringtone.title')
  );

  return (
    <ProtectedPage module="ringtones">
      <div className="ringtone-page-wrapper">
      {/* Shared Header Controls */}
      <SharedHeader
        title={headerTitle}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        searchPlaceholder={selectedOperator ? 'Search related songs...' : t('ringtone.searchPlaceholder')}
        resultCount={selectedOperator ? detailTotalSongs : totalCount}
        resultLabel={selectedOperator ? 'Songs' : t('ringtone.resultsCount')}
        onSortClick={null}
        onFilterClick={null}
        sortActive={false}
        filterActive={false}
        isSortApplied={false}
        isFilterApplied={false}
        onExportClick={handleExportClick}
        onAddNewClick={selectedOperator ? null : (canCreateRingtones ? () => setIsAddingRingtone(true) : null)}
        addNewText={selectedOperator ? null : t('common.addNew')}
        canAddNew={canCreateRingtones}
      />

      {/* Main Section */}
      {!selectedOperator ? (
        <>
          {/* Operators Grid View */}
          <div className="ringtone-grid-container">
            {isLoading ? (
              <LoadingOverlay isLoading message="Fetching ringtones, please wait." />
            ) : ringtones.length > 0 ? (
              ringtones.map((operator) => (
                <RingtoneCard
                  key={operator.id}
                  operator={operator}
                  onSelect={setSelectedOperator}
                  onEdit={canEditRingtones ? handleCardEdit : null}
                  onDelete={canDeleteRingtones ? handleDeleteOperator : null}
                  onInactivate={canEditRingtones ? handleInactivateOperator : null}
                  onActivate={canEditRingtones ? handleActivateOperator : null}
                  t={t}
                />
              ))
            ) : (
              <EmptyState title="No Ringtones Found" subtitle="No ringtone operators match your search." hasSearch={true} />
            )}
          </div>

          {/* Pagination Footer for Initial View */}
          {totalCount > 0 && (
            <div className="ringtone-pagination-row">
              <span className="pagination-range-text">
                {(currentPage - 1) * 20 + 1}-{Math.min(currentPage * 20, totalCount)} of {totalCount}
              </span>
              <div className="pagination-controls-wrapper">
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  &lt;&lt;
                </button>
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.ceil(totalCount / 20) || 1 }, (_, i) => i + 1).map(num => (
                  <button 
                    key={num}
                    type="button" 
                    className={`pager-btn ${currentPage === num ? 'active' : 'outlined'}`}
                    onClick={() => setCurrentPage(num)}
                  >
                    {num}
                  </button>
                ))}
                <button 
                  type="button" 
                  className="pager-btn outlined"
                  disabled={currentPage === (Math.ceil(totalCount / 20) || 1)}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Detailed Song View for Operator */
        <div className="ringtone-detail-table-wrapper">
          {isSongsLoading ? (
            <Loader label="Fetching related songs, please wait." />
          ) : (
            <RingtoneSongTable
              songs={detailSongs}
              currentPage={detailPage}
              totalSongs={detailTotalSongs}
              songsPerPage={20}
              onPageChange={setDetailPage}
              onSelectSong={onSelectSong}
              onScroll={handleRingtoneSongsScroll}
              t={t}
            />
          )}
        </div>
      )}

      {/* ─── Confirmation Modal — Duplicate Name Save ─── */}
      <ConfirmationModal
        isOpen={showConfirmSaveModal}
        title="Ringtone Operator Already Exists Warning"
        message="A ringtone operator with the same name already exists in the database. Are you sure you want to save this operator?"
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowConfirmSaveModal(false)}
      />

      {/* ─── Confirmation Modal — Primary Inactivate ─── */}
      <ConfirmationModal
        isOpen={showConfirmInactivateModal}
        type="warning"
        title="Inactivate Ringtone Operator"
        message={`Are you sure you want to make the operator "${operatorToInactivate?.name}" inactive?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmInactivate}
        onCancel={() => {
          setShowConfirmInactivateModal(false);
          setOperatorToInactivate(null);
        }}
      />

      {/* ─── Confirmation Modal — Primary Activate ─── */}
      <ConfirmationModal
        isOpen={showConfirmActivateModal}
        type="success"
        title="Activate Ringtone Operator"
        message={`Are you sure you want to activate the operator "${operatorToActivate?.name}"?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmActivate}
        onCancel={() => {
          setShowConfirmActivateModal(false);
          setOperatorToActivate(null);
        }}
      />

      {/* ─── Confirmation Modal — Primary Delete ─── */}
      <ConfirmationModal
        isOpen={showConfirmDeleteModal}
        type="danger"
        title="Delete Ringtone Operator"
        message={`Are you sure you want to permanently delete the operator "${operatorToDelete?.name}"?`}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowConfirmDeleteModal(false);
          setOperatorToDelete(null);
        }}
      />

      {/* ─── Dependency Warning — Inactivate ─── */}
      {inactivateDependencyWarningOpen && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 100 }}>
          <div className="modal-card-container" style={{ width: '500px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => {
                setInactivateDependencyWarningOpen(false);
                setOperatorToInactivate(null);
                setPendingInactivateSongs([]);
              }}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div className="modal-icon-container warning" style={{ backgroundColor: '#fef3c7', color: '#d97706', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading" style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>
                  Inactivate Ringtone Warning
                </h3>
                <p className="modal-description-text" style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px' }}>
                  This ringtone is currently assigned to the following active songs. Continuing will make these ringtone relationships inactive as well.
                </p>
              </div>
            </div>

            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '20px', background: '#f8fafc' }}>
              {pendingInactivateSongs.map((song) => (
                <div key={song.id} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{song.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Artist: {song.artist} | Album: {song.album}</div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: '13px', color: '#d97706', fontWeight: '600', marginBottom: '20px' }}>
              Proceeding will inactivate this ringtone and all related song assignments shown above.
            </p>

            <div className="modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => {
                  setInactivateDependencyWarningOpen(false);
                  setOperatorToInactivate(null);
                  setPendingInactivateSongs([]);
                }}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm warning"
                onClick={proceedForceInactivate}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#d97706', color: '#fff', fontWeight: '600' }}
              >
                Confirm Inactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Dependency Warning — Delete ─── */}
      {deleteDependencyWarningOpen && (
        <div className="modal-backdrop-overlay" style={{ zIndex: 100 }}>
          <div className="modal-card-container" style={{ width: '500px', padding: '24px' }} role="dialog" aria-modal="true">
            <button 
              className="modal-close-icon-btn" 
              onClick={() => {
                setDeleteDependencyWarningOpen(false);
                setOperatorToDelete(null);
                setPendingDeleteSongs([]);
              }}
              aria-label="Close dialog"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-content-wrapper" style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
              <div className="modal-icon-container danger" style={{ backgroundColor: '#fee2e2', color: '#ef4444', borderRadius: '50%', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="modal-text-content">
                <h3 className="modal-title-heading" style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0 }}>
                  Delete Ringtone Warning
                </h3>
                <p className="modal-description-text" style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '4px' }}>
                  Permanently deleting this ringtone will also permanently remove all inactive ringtone relationships associated with it.
                </p>
              </div>
            </div>

            <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '20px', background: '#f8fafc' }}>
              {pendingDeleteSongs.map((song) => (
                <div key={song.id} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{song.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Artist: {song.artist} | Album: {song.album}</div>
                </div>
              ))}
            </div>

            <p style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', marginBottom: '20px' }}>
              Deleting this ringtone will also remove its relationship from these songs.
            </p>

            <div className="modal-footer-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={() => {
                  setDeleteDependencyWarningOpen(false);
                  setOperatorToDelete(null);
                  setPendingDeleteSongs([]);
                }}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm danger"
                onClick={proceedForceDelete}
                style={{ cursor: 'pointer', padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: '600' }}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for export operations */}
      <LoadingOverlay isLoading={isExporting} message={t('common.exportingMessage') || 'Exporting data...'} />
      </div>
    </ProtectedPage>
  );
}

export default Ringtone;
